<mat-card>
  <mat-card-header>
    <mat-card-title>Central payees</mat-card-title>
  </mat-card-header>

  <mat-card-content>
    <p class="explanation">
      Some charities are not paid directly: several teams may share one bank account, or an umbrella
      organization may take a single payment and distribute it onward. A payee is the name on that cheque — it
      earns nothing itself. The money still belongs to the charities that earned it, so a payment to a payee is
      recorded as one payout per charity and each charity's balance comes down by its own share.
    </p>

    <ng-container *hasPermission="permissionTypes.ModifyCharities">
      <form [formGroup]="newPayeeForm" (ngSubmit)="onCreatePayee()" class="new-payee-form">
        <mat-form-field class="payee-name-field">
          <mat-label>Payee name</mat-label>
          <input matInput
                 maxlength="200"
                 placeholder="Holland College"
                 formControlName="name"
                 data-testid="new-payee-name-input">
          @if (newPayeeForm.controls.name.touched && newPayeeForm.controls.name.invalid) {
            <mat-error>Enter a name for this payee.</mat-error>
          }
        </mat-form-field>

        <button mat-flat-button
                color="primary"
                type="submit"
                [disabled]="newPayeeForm.invalid || isSubmitting"
                data-testid="add-payee-button">
          Add payee
        </button>
      </form>
    </ng-container>

    @if (uiState === UIStateEnum.ShowLoading) {
      <app-loading-card-content></app-loading-card-content>
    } @else if (payees.length) {
      <app-table-export-buttons
        testIdPrefix="payees"
        (exportCsv)="onExportPayeesCsv()"
        (exportPdf)="onExportPayeesPdf()">
      </app-table-export-buttons>

      <table class="payees-table" data-testid="payees-table">
        <thead>
          <tr>
            <th>Payee</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @for (payee of payees; track payee.Id) {
            <tr data-testid="payee-row"
                [class.inactive]="!payee.Active"
                [class.selected]="selectedPayee?.Id === payee.Id">
              <td>{{ payee.Name }}</td>
              <!-- Deactivated payees stay listed: payouts already made to them still name them. -->
              <td>{{ payee.Active ? 'Active' : 'Inactive' }}</td>
              <td class="actions">
                @if (payee.Active) {
                  <button mat-stroked-button
                          type="button"
                          (click)="onSelectPayee(payee)"
                          [attr.data-testid]="'select-payee-' + payee.Id">
                    Record a payment
                  </button>
                }
                <ng-container *hasPermission="permissionTypes.ModifyCharities">
                  <button mat-stroked-button
                          type="button"
                          (click)="onToggleActive(payee)"
                          [attr.data-testid]="'toggle-payee-' + payee.Id">
                    {{ payee.Active ? 'Deactivate' : 'Reactivate' }}
                  </button>
                </ng-container>
              </td>
            </tr>
          }
        </tbody>
      </table>
    } @else {
      <p class="empty" data-testid="payees-empty">
        No central payees yet. Every charity in this game is paid directly.
      </p>
    }
  </mat-card-content>
</mat-card>

<!-- Record a payment sits directly under the payee list, ABOVE the assignment section: the charity list
     below can run to hundreds of rows, and a panel rendered after it looked like nothing had happened
     when "Record a payment" was clicked (#1506). Applies to the no-members message inside it too. -->
@if (selectedPayee) {
  <mat-card class="mt-4" data-testid="record-payee-payment-card">
    <mat-card-header>
      <mat-card-title>Record a payment to {{ selectedPayee.Name }}</mat-card-title>
    </mat-card-header>

    <mat-card-content>
      @if (members.length) {
        <p class="explanation">
          This payment will be split across the charities below, in proportion to what each is owed, and
          recorded as one payout each. The parts always add up to the amount entered.
        </p>

        <table class="members-table" data-testid="payee-members-table">
          <thead>
            <tr>
              <th>Charity</th>
              <th class="num">Owed</th>
            </tr>
          </thead>
          <tbody>
            @for (member of members; track member.CharityId) {
              <tr data-testid="payee-member-row">
                <td>{{ member.CharityName }}{{ member.CharityActive ? '' : ' (inactive)' }}</td>
                <td class="num">{{ member.AmountOwed | formatCurrency }}</td>
              </tr>
            }
          </tbody>
          <tfoot>
            <tr>
              <td>Total owed</td>
              <td class="num" data-testid="payee-total-owed">{{ totalOwedToMembers | formatCurrency }}</td>
            </tr>
          </tfoot>
        </table>

        <ng-container *hasPermission="permissionTypes.ModifyCharityPayouts">
          <form [formGroup]="paymentForm" (ngSubmit)="onRecordPayment()" class="payment-form">
            <mat-form-field>
              <mat-label>Amount ($)</mat-label>
              <input matInput
                     type="number"
                     min="0.01"
                     step="0.01"
                     formControlName="amount"
                     data-testid="payee-payment-amount-input">
              @if (paymentForm.controls.amount.touched && paymentForm.controls.amount.invalid) {
                <mat-error>Enter an amount greater than zero.</mat-error>
              }
            </mat-form-field>

            <button mat-button
                    type="button"
                    (click)="onFillTotalOwed()"
                    data-testid="payee-fill-total-owed-button">
              Fill total owed
            </button>

            <mat-form-field>
              <mat-label>Payment date</mat-label>
              <input matInput
                     type="date"
                     [max]="today"
                     formControlName="paymentDate"
                     data-testid="payee-payment-date-input">
            </mat-form-field>

            <mat-form-field>
              <mat-label>Reference #</mat-label>
              <input matInput
                     maxlength="100"
                     formControlName="referenceNumber"
                     data-testid="payee-payment-reference-input">
            </mat-form-field>

            <mat-form-field class="note-field">
              <mat-label>{{ isOverpayment ? 'Note (required)' : 'Note' }}</mat-label>
              <input matInput formControlName="note" data-testid="payee-payment-note-input">
            </mat-form-field>

            <!-- What is owed is settled first and the excess is spread evenly across every member, so an
                 overpayment leaves each of them holding a credit. Saying so before the cheque is recorded is the
                 difference between a deliberate overpayment and a typo nobody notices until the next period. -->
            @if (isOverpayment) {
              <p class="payment-warning" data-testid="payee-overpayment-warning">
                This is {{ overpaymentCents | formatCurrency }} more than these charities are owed
                ({{ totalOwedToMembers | formatCurrency }} in total). The excess will be spread evenly across all
                {{ members.length }} of them as a credit, so a note explaining it is required.
              </p>
            }

            <button mat-flat-button
                    color="primary"
                    type="submit"
                    [disabled]="!canRecordPayment"
                    data-testid="record-payee-payment-button">
              Record payment
            </button>
          </form>
        </ng-container>
      } @else {
        <!-- The money would have nowhere truthful to land, so the payment is refused rather than attributed
             to a charity nobody named. -->
        <p class="empty" data-testid="payee-no-members">
          No charities are assigned to {{ selectedPayee.Name }} yet, so there is nothing for a payment to cover.
          Assign one above first.
        </p>
      }

      @if (payments.length) {
        <h4 class="split-heading">Payments already made to {{ selectedPayee.Name }}</h4>
        <!-- Voiding is offered per payment, never per charity: the rows of one payment are one cheque, so a
             mis-keyed amount is corrected by voiding the payment and recording it again. -->
        <table class="members-table" data-testid="payee-payments-table">
          <thead>
            <tr>
              <th>Recorded</th>
              <th>Payment date</th>
              <th>Reference #</th>
              <th class="num">Charities</th>
              <th class="num">Amount</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            @for (payment of payments; track payment.PayoutBatchId) {
              <tr data-testid="payee-payment-row" [class.voided]="payment.Voided">
                <td>{{ payment.RecordedOn | date: 'MMM d, y' }}</td>
                <td>{{ payment.PaymentDate ? (payment.PaymentDate | date: 'MMM d, y') : '—' }}</td>
                <td>{{ payment.ReferenceNumber || '—' }}</td>
                <td class="num">{{ payment.CharityCount }}</td>
                <td class="num">{{ payment.TotalAmount | formatCurrency }}</td>
                <td>{{ payment.Voided ? 'Voided' : 'Applied' }}</td>
                <td>
                  @if (!payment.Voided) {
                    <ng-container *hasPermission="permissionTypes.ModifyCharityPayouts">
                      <button mat-stroked-button
                              type="button"
                              (click)="onVoidPayment(payment)"
                              data-testid="void-payee-payment-button">
                        Void payment
                      </button>
                    </ng-container>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
      }

      @if (lastRecordedAllocations.length) {
        <h4 class="split-heading">How the last payment was split</h4>
        <table class="members-table" data-testid="payee-last-split-table">
          <thead>
            <tr>
              <th>Charity</th>
              <th class="num">Was owed</th>
              <th class="num">Paid</th>
            </tr>
          </thead>
          <tbody>
            @for (allocation of lastRecordedAllocations; track allocation.CharityId) {
              <tr data-testid="payee-split-row">
                <td>{{ allocation.CharityName }}</td>
                <td class="num">{{ allocation.AmountOwedBefore | formatCurrency }}</td>
                <td class="num">{{ allocation.AmountPaid | formatCurrency }}</td>
              </tr>
            }
          </tbody>
        </table>
      }
    </mat-card-content>
  </mat-card>
}

<!-- Assignment: the "inform during charity setup, and change it later" half of the ask. -->
<mat-card class="mt-4" *hasPermission="permissionTypes.ModifyCharities" data-testid="charity-payee-assignment-card">
  <mat-card-header>
    <mat-card-title>Who each charity is paid through</mat-card-title>
  </mat-card-header>

  <mat-card-content>
    <p class="explanation">
      Changing this affects future payouts only. Payouts already recorded keep the payee they were actually
      paid to, so the ledger still matches the bank.
    </p>

    @if (assignments.length) {
      <app-table-export-buttons
        testIdPrefix="charity-payee-assignments"
        (exportCsv)="onExportAssignmentsCsv()"
        (exportPdf)="onExportAssignmentsPdf()">
      </app-table-export-buttons>

      <table class="assignment-table" data-testid="charity-payee-assignment-table">
        <thead>
          <tr>
            <th>Charity</th>
            <th>Paid through</th>
          </tr>
        </thead>
        <tbody>
          @for (assignment of assignments; track assignment.CharityId) {
            <tr data-testid="charity-payee-row">
              <td>{{ assignment.CharityName }}{{ assignment.CharityActive ? '' : ' (inactive)' }}</td>
              <td>
                <mat-form-field class="payee-select-field">
                  <mat-label>Payee</mat-label>
                  <mat-select [value]="assignment.PayeeId ?? ''"
                              (valueChange)="onAssignCharity(assignment, $event)"
                              [attr.data-testid]="'charity-payee-select-' + assignment.CharityId">
                    <mat-option value="">Paid directly</mat-option>
                    @for (payee of activePayees; track payee.Id) {
                      <mat-option [value]="payee.Id">{{ payee.Name }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </td>
            </tr>
          }
        </tbody>
      </table>
    } @else {
      <p class="empty">This game has no charities yet.</p>
    }
  </mat-card-content>
</mat-card>
